/**
 * Global AI Assistant (F045)
 *
 * Floating assistant panel with streaming chat, persona-aware prompts,
 * and contextual suggestions.
 */
import React, { useState, useRef, useCallback } from 'react';
import {
  Card,
  CardHeader,
  Body1,
  Body2,
  Button,
  Input,
  Spinner,
  tokens,
  makeStyles,
  Badge,
} from '@fluentui/react-components';
import {
  ChatBubblesQuestionRegular,
  DismissRegular,
  SendRegular,
  SparkleRegular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { assistantApi } from '../../services/api';
import { usePersona } from '../../features/persona';
import type { AssistantContext } from '../../types';

const useStyles = makeStyles({
  trigger: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    zIndex: 1000,
    borderRadius: '50%',
    width: '56px',
    height: '56px',
    minWidth: '56px',
  },
  panel: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    width: '420px',
    maxHeight: '600px',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: tokens.shadow16,
    borderRadius: tokens.borderRadiusXLarge,
    overflow: 'hidden',
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    maxHeight: '400px',
  },
  userMsg: {
    alignSelf: 'flex-end',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusLarge,
    maxWidth: '80%',
  },
  assistantMsg: {
    alignSelf: 'flex-start',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusLarge,
    maxWidth: '80%',
  },
  inputRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  suggestions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
  },
});

interface ChatMsg {
  role: 'user' | 'assistant';
  content: string;
}

export const GlobalAssistant: React.FC<{ currentPage?: string }> = ({ currentPage }) => {
  const styles = useStyles();
  const { persona } = usePersona();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: context } = useQuery<AssistantContext>({
    queryKey: ['assistantContext', currentPage, persona],
    queryFn: () => assistantApi.getContext(currentPage, persona),
    enabled: isOpen,
    staleTime: 60_000,
  });

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isStreaming) return;

    const userMsg: ChatMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    let assistantText = '';
    setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

    try {
      await assistantApi.sendMessageStream(
        {
          message: text,
          persona,
          currentPage,
          history: messages.map(m => ({ role: m.role, content: m.content })),
        },
        {
          onToken: (token) => {
            assistantText += token;
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: assistantText };
              return updated;
            });
            scrollToBottom();
          },
          onDone: () => {
            setIsStreaming(false);
          },
          onError: (error) => {
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: `Error: ${error}` };
              return updated;
            });
            setIsStreaming(false);
          },
        },
        controller.signal,
      );
    } catch {
      setIsStreaming(false);
    }
  }, [messages, persona, currentPage, isStreaming, scrollToBottom]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  if (!isOpen) {
    return (
      <Button
        className={styles.trigger}
        appearance="primary"
        icon={<ChatBubblesQuestionRegular />}
        onClick={() => setIsOpen(true)}
        title="Open AI Assistant"
      />
    );
  }

  return (
    <Card className={styles.panel}>
      <CardHeader
        image={<SparkleRegular style={{ fontSize: '1.25rem', color: tokens.colorBrandForeground1 }} />}
        header={<Body1 style={{ fontWeight: 600 }}>LeftShield Assistant</Body1>}
        description={<Body2 style={{ color: tokens.colorNeutralForeground3 }}>Persona: {persona}</Body2>}
        action={
          <Button
            appearance="subtle"
            icon={<DismissRegular />}
            onClick={() => setIsOpen(false)}
            size="small"
          />
        }
      />

      <div className={styles.messages}>
        {messages.length === 0 && context?.suggestions && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
            <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Suggested questions:</Body2>
            <div className={styles.suggestions}>
              {context.suggestions.map((s, i) => (
                <Badge
                  key={i}
                  appearance="outline"
                  style={{ cursor: 'pointer' }}
                  onClick={() => sendMessage(s)}
                >
                  {s}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={msg.role === 'user' ? styles.userMsg : styles.assistantMsg}>
            <Body2>{msg.content || (isStreaming && i === messages.length - 1 ? <Spinner size="tiny" /> : '')}</Body2>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className={styles.inputRow}>
        <Input
          style={{ flex: 1 }}
          placeholder="Ask about your governance posture..."
          value={input}
          onChange={(_, d) => setInput(d.value)}
          onKeyDown={handleKeyDown}
          disabled={isStreaming}
        />
        <Button
          appearance="primary"
          icon={<SendRegular />}
          onClick={() => sendMessage(input)}
          disabled={isStreaming || !input.trim()}
        />
      </div>
    </Card>
  );
};
